import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation, faHeartPulse, faFire, faShieldHalved,
  faLocationDot, faCheckCircle, faRefresh, faCircleCheck,
  faSpinner, faUser, faBuilding, faChartBar
} from '@fortawesome/free-solid-svg-icons';
import Navigation from '../Layouts/Navigation/Navigation';
import { BASE_URL } from '../utils/constants';

/* ── helpers ── */
const getUserId = () => {
  try {
    const t = localStorage.getItem('token');
    if (!t) return null;
    return JSON.parse(atob(t.split('.')[1])).id_usuario;
  } catch { return null; }
};

const TIPOS = {
  panico:    { label: 'Pánico',               icon: faTriangleExclamation, color: 'bg-red-600',    badge: 'bg-red-100 text-red-700 border-red-200',        bar: 'border-red-500',   stat: 'bg-red-50 text-red-600 border-red-200'    },
  medica:    { label: 'Emergencia Médica',    icon: faHeartPulse,          color: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700 border-orange-200', bar: 'border-orange-500', stat: 'bg-orange-50 text-orange-600 border-orange-200' },
  incendio:  { label: 'Incendio',             icon: faFire,                color: 'bg-amber-500',  badge: 'bg-amber-100 text-amber-700 border-amber-200',    bar: 'border-amber-500', stat: 'bg-amber-50 text-amber-600 border-amber-200'   },
  seguridad: { label: 'Situación Seguridad',  icon: faShieldHalved,        color: 'bg-blue-700',   badge: 'bg-blue-100 text-blue-700 border-blue-200',       bar: 'border-blue-600',  stat: 'bg-blue-50 text-blue-700 border-blue-200'      },
};

const ESTADO_BADGE = {
  activa:    'bg-red-100 text-red-700 border border-red-200',
  en_camino: 'bg-purple-100 text-purple-700 border border-purple-200',
  atendida:  'bg-yellow-100 text-yellow-700 border border-yellow-200',
  resuelta:  'bg-green-100 text-green-700 border border-green-200',
};

const ESTADO_LABEL = {
  activa:    'Activa',
  en_camino: 'En camino',
  atendida:  'Atendida',
  resuelta:  'Resuelta',
};

const FILTER_STATES = ['todos', 'activa', 'en_camino', 'atendida', 'resuelta'];
const FILTER_TIPOS  = ['todos', 'panico', 'medica', 'incendio', 'seguridad'];

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()} · ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
};

const Snack = ({ msg, type, onClose }) => {
  if (!msg) return null;
  const colors = { success: 'bg-green-600', error: 'bg-red-600', info: 'bg-blue-600' };
  return (
    <div className={`fixed top-5 right-5 z-50 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 ${colors[type] || colors.info}`}>
      <span>{msg}</span>
      <button onClick={onClose} className="font-bold">✕</button>
    </div>
  );
};

export default function AdminEmergencias() {
  const userId = getUserId();
  const [emergencias, setEmergencias] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [filterEstado, setFilterEstado] = useState('todos');
  const [filterTipo, setFilterTipo] = useState('todos');
  const [snack, setSnack] = useState({ msg: '', type: 'info' });

  const showSnack = (msg, type = 'info') => {
    setSnack({ msg, type });
    setTimeout(() => setSnack({ msg: '' }), 4000);
  };

  const loadData = useCallback(async () => {
    try {
      const [emRes, stRes] = await Promise.all([
        axios.get(`${BASE_URL}/emergencias?limit=200`),
        axios.get(`${BASE_URL}/emergencias/stats`),
      ]);
      setEmergencias(emRes.data);
      setStats(stRes.data);
    } catch {
      showSnack('Error al cargar emergencias', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleResolver = async (id) => {
    setActionLoading(id);
    try {
      await axios.put(`${BASE_URL}/emergencias/${id}/resolver`, { id_usuario_resuelve: userId });
      showSnack('✅ Emergencia resuelta', 'success');
      loadData();
    } catch (err) {
      showSnack(err.response?.data?.error || 'Error al resolver', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // Filtros
  const filtered = emergencias.filter(e => {
    if (filterEstado !== 'todos' && e.estado !== filterEstado) return false;
    if (filterTipo !== 'todos' && e.tipo !== filterTipo) return false;
    return true;
  });

  return (
    <>
      <Navigation />
      <Snack msg={snack.msg} type={snack.type} onClose={() => setSnack({ msg: '' })} />

      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-5xl mx-auto space-y-6">

          {/* Header */}
          <div className="bg-red-600 rounded-2xl p-6 text-white shadow-lg flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faTriangleExclamation} className="text-3xl" />
                <h1 className="text-2xl font-bold">Gestión de Emergencias</h1>
              </div>
              <p className="text-red-100 text-sm mt-1">Panel de administración · Últimos 30 días</p>
            </div>
            <button
              onClick={loadData}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <FontAwesomeIcon icon={faRefresh} /> Actualizar
            </button>
          </div>

          {/* Stats cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Estado resumen */}
              <div className="bg-white rounded-2xl shadow-sm p-4 text-center border border-gray-100">
                <p className="text-3xl font-bold text-gray-800">{stats.total || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Total emergencias</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-2xl shadow-sm p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{stats.activas || 0}</p>
                <p className="text-xs text-red-500 mt-1">Activas ahora</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl shadow-sm p-4 text-center">
                <p className="text-3xl font-bold text-yellow-600">{stats.atendidas || 0}</p>
                <p className="text-xs text-yellow-600 mt-1">En atención</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-2xl shadow-sm p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{stats.resueltas || 0}</p>
                <p className="text-xs text-green-600 mt-1">Resueltas</p>
              </div>
              {/* Por tipo */}
              {Object.entries(TIPOS).map(([key, meta]) => (
                <div key={key} className={`rounded-2xl border shadow-sm p-4 text-center ${meta.stat}`}>
                  <FontAwesomeIcon icon={meta.icon} className="text-xl mb-1" />
                  <p className="text-2xl font-bold">{stats[key] || 0}</p>
                  <p className="text-xs mt-0.5">{meta.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Filtros */}
          <div className="bg-white rounded-2xl shadow-sm p-4 flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faChartBar} className="text-gray-400 text-sm" />
              <span className="text-sm font-semibold text-gray-600">Estado:</span>
              <div className="flex gap-1">
                {FILTER_STATES.map(s => (
                  <button
                    key={s}
                    onClick={() => setFilterEstado(s)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${filterEstado === s ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-600">Tipo:</span>
              <div className="flex gap-1 flex-wrap">
                {FILTER_TIPOS.map(t => (
                  <button
                    key={t}
                    onClick={() => setFilterTipo(t)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${filterTipo === t ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {t === 'todos' ? 'Todos' : (TIPOS[t]?.label || t)}
                  </button>
                ))}
              </div>
            </div>
            <span className="ml-auto text-xs text-gray-400">{filtered.length} registro{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Lista */}
          {loading ? (
            <div className="flex justify-center py-16">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-red-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-md p-12 text-center">
              <FontAwesomeIcon icon={faCheckCircle} className="text-5xl text-green-400 mb-3" />
              <p className="text-gray-500">No hay emergencias con los filtros seleccionados</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filtered.map(e => {
                const meta = TIPOS[e.tipo] || TIPOS.panico;
                return (
                  <div key={e.id_emergencia} className={`bg-white rounded-2xl shadow-md border-l-4 ${meta.bar} p-5`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      {/* Tipo + estado */}
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full ${meta.color} flex items-center justify-center text-white shadow-md flex-shrink-0`}>
                          <FontAwesomeIcon icon={meta.icon} className="text-xl" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-800">{meta.label}</p>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${ESTADO_BADGE[e.estado]}`}>
                            {ESTADO_LABEL[e.estado] || e.estado}
                          </span>
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-400">
                        <p>{fmtDate(e.fecha_emergencia)}</p>
                        <p className="font-mono text-gray-300">#{e.id_emergencia}</p>
                      </div>
                    </div>

                    {/* Residente */}
                    <div className="mt-3 bg-gray-50 rounded-xl p-3 space-y-1">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <FontAwesomeIcon icon={faUser} className="text-gray-400 w-4" />
                        <span className="font-semibold">{e.usuario_nombre} {e.usuario_apellido}</span>
                        {e.usuario_telefono && <span className="text-gray-500">— {e.usuario_telefono}</span>}
                      </div>
                      {e.num_apartamento && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FontAwesomeIcon icon={faBuilding} className="text-gray-400 w-4" />
                          <span>Apto {e.num_apartamento}{e.torre_nombre ? ` — Torre ${e.torre_nombre}` : ''}</span>
                        </div>
                      )}
                      {e.descripcion && <p className="text-sm text-gray-600 italic">"{e.descripcion}"</p>}
                    </div>

                    {/* Ubicación */}
                    {e.latitud && e.longitud && (
                      <a
                        href={`https://www.google.com/maps?q=${e.latitud},${e.longitud}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 flex items-center gap-2 text-sm text-blue-600 hover:underline"
                      >
                        <FontAwesomeIcon icon={faLocationDot} /> Ver ubicación en el mapa
                      </a>
                    )}

                    {/* Atendida por */}
                    {e.atendido_por_nombre && (
                      <p className="text-xs text-green-600 mt-2">
                        Atendida por: {e.atendido_por_nombre} {e.atendido_por_apellido} · {fmtDate(e.fecha_atencion)}
                      </p>
                    )}

                    {/* Acción resolver */}
                    {e.estado !== 'resuelta' && (
                      <div className="mt-3">
                        <button
                          onClick={() => handleResolver(e.id_emergencia)}
                          disabled={actionLoading === e.id_emergencia}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl disabled:opacity-50 transition-colors"
                        >
                          {actionLoading === e.id_emergencia
                            ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                            : <><FontAwesomeIcon icon={faCircleCheck} className="mr-1" />Marcar como resuelta</>}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
